import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

/* ─── Styled Components ─── */

const GateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 40px 20px;
  background: ${p => p.theme?.colors?.background || '#FFFDF7'};
`;

const GateCard = styled.div`
  max-width: 480px;
  width: 100%;
  padding: 40px 32px;
  background: ${p => p.theme?.colors?.surface || '#FFFFFF'};
  border-radius: ${p => (p.theme?.radius?.lg || 12) + 'px'};
  box-shadow: ${p => p.theme?.shadow?.card || '0 4px 20px rgba(0,0,0,0.08)'};
  text-align: center;
`;

const ToolIconDisplay = styled.div`
  font-size: 3rem;
  margin-bottom: 16px;
  line-height: 1;
`;

const ToolNameDisplay = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${p => p.theme?.colors?.text?.primary || '#2C1810'};
  margin: 0 0 12px;
  font-family: 'Playfair Display', Georgia, serif;
`;

const FreeBadge = styled.span`
  display: inline-block;
  background: ${p => p.theme?.colors?.bands?.exceptional || '#DAA520'};
  color: #FFFFFF;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
`;

const DescriptionText = styled.p`
  color: ${p => p.theme?.colors?.text?.secondary || '#9B8B7A'};
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 24px;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(90deg, transparent, ${p => p.theme?.colors?.bands?.exceptional || '#DAA520'}, transparent);
  margin: 0 0 24px;
`;

const PromptText = styled.p`
  font-weight: 600;
  color: ${p => p.theme?.colors?.text?.primary || '#2C1810'};
  font-size: 1rem;
  margin: 0 0 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const BaseButton = styled.button`
  padding: 12px 28px;
  border-radius: ${p => (p.theme?.radius?.md || 8) + 'px'};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  min-width: 140px;

  @media (max-width: 400px) {
    width: 100%;
  }
`;

const LoginButton = styled(BaseButton)`
  background: none;
  border: 2px solid ${p => p.theme?.colors?.bands?.exceptional || '#DAA520'};
  color: ${p => p.theme?.colors?.bands?.exceptional || '#DAA520'};

  &:hover {
    background: rgba(218, 165, 32, 0.08);
  }
`;

const SignUpButton = styled(BaseButton)`
  background: ${p => p.theme?.colors?.bands?.exceptional || '#DAA520'};
  border: 2px solid ${p => p.theme?.colors?.bands?.exceptional || '#DAA520'};
  color: #FFFFFF;

  &:hover {
    background: ${p => p.theme?.colors?.bands?.exceptional || '#B8860B'};
    filter: brightness(0.9);
  }
`;

/* ─── Component ─── */

/**
 * SoftAuthGate: Shown to unauthenticated visitors on free tool pages.
 * Instead of a hard redirect to /login, this shows a friendly prompt
 * with the tool name, description, and Login/SignUp CTAs.
 */
const SoftAuthGate = ({ toolName, toolDescription, toolIcon }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Authenticated users never see the gate
  if (isAuthenticated) {
    return null;
  }

  const currentPath = location.pathname;
  const redirectParam = encodeURIComponent(currentPath);

  return (
    <GateContainer>
      <GateCard>
        {toolIcon && <ToolIconDisplay>{toolIcon}</ToolIconDisplay>}
        <ToolNameDisplay>{toolName}</ToolNameDisplay>
        <FreeBadge>Free Tool</FreeBadge>
        <DescriptionText>{toolDescription}</DescriptionText>
        <Divider />
        <PromptText>Sign up or log in to access this free tool</PromptText>
        <ButtonGroup>
          <LoginButton
            onClick={() => navigate(`/login?redirect=${redirectParam}`)}
            aria-label={`Log in to access ${toolName}`}
          >
            Log In
          </LoginButton>
          <SignUpButton
            onClick={() => navigate(`/register?redirect=${redirectParam}`)}
            aria-label={`Sign up to access ${toolName}`}
          >
            Sign Up Free
          </SignUpButton>
        </ButtonGroup>
      </GateCard>
    </GateContainer>
  );
};

SoftAuthGate.propTypes = {
  toolName: PropTypes.string.isRequired,
  toolDescription: PropTypes.string.isRequired,
  toolIcon: PropTypes.string,
};

SoftAuthGate.defaultProps = {
  toolIcon: '',
};

export default SoftAuthGate;
